/**
 * The per-tool "ecosystem" paths, extracted from fifteen near-identical page
 * components that between them carried 2,460 lines of duplicated inline style.
 *
 * Each tool is a short ordered path rather than a set of alternatives. The old
 * pages lettered the options A/B/C, which reads as "pick one and leave"; that
 * is why nobody ever came back to them. Numbering them makes the page
 * something you work through, which is what gives a saved account a job.
 *
 * `state: "soon"` renders a locked row with a notify control. Nothing uses it
 * today: the eleven placeholder steps that existed were removed on 26 Aug
 * 2026, Daniel's call, because a path that ends on a greyed-out row and a
 * "tell me when this is ready" reads as an unfinished page rather than a
 * short one.
 *
 * The state is kept because it is the right answer for a guide that is
 * genuinely being written and about to land. It is the wrong answer for a
 * placeholder standing in for a guide nobody has started, which is what all
 * eleven were.
 *
 * The cost is real and worth stating: agent-opus, gamma and gohighlevel are
 * now a single row each, the outbound link and nothing else. That is honest
 * about what exists rather than padding, but those three paths are doing no
 * work until they have a second step.
 *
 * Grok was a fourth until 2 Sep 2026, when it was removed from the site
 * outright. It had been unpublished since 24 Aug; the page and the tool went
 * together.
 */

export type StepState = "live" | "soon";

/**
 * Where "join" goes, and what it is called.
 *
 * Written here rather than in the component because the anchor is a section
 * on a marketing site this repo does not own — when that page is restructured
 * the link breaks silently, and one constant is one place to fix it rather
 * than five copies inside JSX. The name and price are alongside it for the
 * same reason: the blurb quotes both, and a price that disagrees with the
 * page it links to is worse than no price at all.
 *
 * NOTE: $19 is what yoursmartstack.com advertises. PRODUCTS.MEMBERSHIP in
 * server/billing/products.ts still says $29, but nothing on this site calls
 * createCheckout, so the two have never been shown to the same person. If a
 * pricing page is ever built here, that has to be settled first.
 */
export const MEMBERSHIP = {
  name: "Your Smart Stack +",
  price: "$19 a month",
  href: "https://yoursmartstack.com/#plus",
  /**
   * Where somebody who has decided goes. `href` above is the explainer section
   * on the marketing page; this is the page that takes the money.
   *
   * Worth sending a signed-in person here rather than to the anchor: the
   * checkout page runs the account gate, the session cookie is scoped to
   * .yoursmartstack.com, so they arrive already recognised and skip the form.
   */
  checkoutHref: "https://yoursmartstack.com/checkout/plus/",
} as const;

/**
 * The paid companion app at the end of a tool's path.
 *
 * Only five tools have one. The step renders differently from every other
 * step on the page: it is the only one whose content depends on who is
 * looking. Someone who owns the app gets a plain way in; someone who does not
 * gets the two ways to get it. Both were previously the same bare link, so a
 * member who had already paid was being sold something they owned, and a
 * visitor who had not was being dropped onto a sign-in wall with no
 * explanation of what was behind it.
 */
export interface AppOffer {
  /**
   * `products.key`, which is what billing.myApps reports back. Ownership is a
   * lookup against that list, so these strings are a contract with rows in the
   * database — not a label. Get one wrong and the owner branch simply never
   * renders, which looks exactly like "not entitled" and reports no error.
   *
   * These are the keys the rows were ALREADY sold under, read out of
   * production on 26 Aug 2026: four apps at `<tool>-companion-app`, and Claude
   * at 'quickwins'. They were shorter here first, which matched nothing and
   * would have left every purchaser looking un-entitled. The database is the
   * authority, not this file — a key that has been sold is in purchases rows
   * and renaming it revokes access.
   */
  productKey: string;
  /**
   * What the app is called. Not `ToolPath.name`, which is the tool ("Claude"),
   * sometimes lowercase, and in two cases still the slug.
   *
   * "... Companion App", matching products.name for quickwins, which migration
   * 0008 set to "Claude Companion App". The storefront's cards say "Claude
   * Companion" without the noun; here the name has to survive on its own in
   * "Open Claude Companion" and in a receipt, where "Companion" alone reads as
   * an adjective looking for something to modify.
   */
  name: string;
  /** Where somebody who does not own it buys this one app. */
  checkoutUrl: string;
  /** The one-time price, written out, because the button quotes it. */
  price: string;
  /**
   * What this app is for, in one line. Caren's, from the storefront's own
   * card for it, so the two places that sell it say the same sentence.
   *
   * This is the only part of the card that differs between the five. What a
   * companion app *is* does not change from app to app, which is why the
   * three bullets under it are shared rather than five sets of near-identical
   * lines that would drift apart the first time one was edited.
   */
  blurb: string;
}

export interface PathStep {
  /** Stable within a tool. Used as the progress key, so never renumber. */
  id: string;
  title: string;
  sub: string;
  /** Short label for what the step actually gives you. */
  tag: string;
  state: StepState;
  href: string;
  /** External steps open a new tab and cannot self-report completion. */
  external: boolean;
  /** Rough time cost, shown next to the action. */
  minutes?: number;
  /**
   * Set only on the companion-app step, which is always the last step on the
   * path it belongs to. The component keys the entitlement-aware rendering off
   * this being present rather than off the index, so a tool that later grows a
   * sixth guide keeps working as long as the app step stays at the end.
   */
  app?: AppOffer;
}

export interface ToolPath {
  slug: string;
  /** Tool name as it appears in the eyebrow and prompts. */
  name: string;
  headline: string;
  headlineAlt: string;
  steps: PathStep[];
}

/**
 * What every companion app gives you, whichever tool it is for.
 *
 * Caren's words, lifted from the "Your Smart Stack +" panel on
 * yoursmartstack.com, so somebody who read the storefront and then landed
 * here is told the same thing twice rather than two different things.
 *
 * Three of the six on that panel. The other three — updated when the tools
 * change, your stack connected, cancel in two clicks — are about the
 * membership rather than the app, and this card is where somebody decides
 * whether the app is worth $147 on its own.
 */
export const APP_BENEFITS = [
  "Built for how you work: no jargon, nothing to go look up",
  "Task-first, not feature-first: ten minutes, not ten hours",
  "Copy-and-go prompts you can change until they sound like you",
] as const;

export const TOOL_PATHS: Record<string, ToolPath> = {
  "agent-opus": {
    slug: "agent-opus",
    name: "Agent Opus",
    headline: "Full videos,",
    headlineAlt: "from a single idea.",
    steps: [
      {
        id: "agent-opus-1",
        title: "I want to try Agent Opus: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Agent Opus",
        state: "live",
        href: "https://www.opus.pro/agent?ref_id=9J4U2I5N5",
        external: true,
      },
    ],
  },
  "canva": {
    slug: "canva",
    name: "Canva AI",
    headline: "Design like a pro,",
    headlineAlt: "without the learning curve.",
    steps: [
      {
        id: "canva-1",
        title: "I want to try Canva AI: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Canva AI",
        state: "live",
        href: "https://www.canva.com/",
        external: true,
      },
      {
        /* Reusing a locked placeholder's id, which is safe precisely because
           it was locked: a locked step renders no completion control, so
           nothing can have been ticked against it. */
        id: "canva-2",
        title: "I want to create professional visuals without a designer",
        sub: "Five steps to your first social image, all on the free plan",
        tag: "5-Step Cheat Sheet",
        state: "live",
        href: "/resources/canva/basics",
        external: false,
      },
    ],
  },
  "base44": {
    slug: "base44",
    name: "Base44",
    headline: "Describe the app,",
    headlineAlt: "and have it built tonight.",
    steps: [
      {
        id: "base44-1",
        title: "I want to try Base44: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Base44",
        state: "live",
        href: "https://base44.com/",
        external: true,
      },
      {
        id: "base44-2",
        title: "I want to understand 8 ways to think with Base44",
        sub: "Because the fastest way to a working app is letting go of every decision that is not the idea itself",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/base44/8-ways",
        external: false,
      },
    ],
  },
  "castmagic": {
    slug: "castmagic",
    name: "Castmagic",
    headline: "One recording,",
    headlineAlt: "a whole week of content.",
    steps: [
      {
        id: "castmagic-1",
        title: "I want to try Castmagic, take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Castmagic",
        state: "live",
        href: "https://get.castmagic.io/carenglasser",
        external: true,
      },
      {
        id: "castmagic-2",
        title: "I want Castmagic to sound exactly like me",
        sub: "I need a brand voice framework so every output feels authentic, not generic AI",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/castmagic/brand-voice",
        external: false,
      },
    ],
  },
  "chatgpt": {
    slug: "chatgpt",
    name: "ChatGPT",
    headline: "Start with ChatGPT",
    headlineAlt: "the way that actually sticks.",
    steps: [
      {
        id: "chatgpt-1",
        title: "I want to try ChatGPT: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit ChatGPT",
        state: "live",
        href: "https://chatgpt.com/",
        external: true,
      },
      {
        id: "chatgpt-2",
        title: "I want to understand what ChatGPT can actually do for me",
        sub: "I've heard the hype but haven't found my use case yet",
        tag: "5-Step Cheat Sheet",
        state: "live",
        href: "/chatgpt-basics",
        external: false,
      },
      {
        id: "chatgpt-3",
        title: "I'm ready to build a real workflow with ChatGPT",
        sub: "I want prompts and systems I can use every day",
        tag: "13 Quick Wins",
        state: "live",
        href: "/chatgpt-quick-wins",
        external: false,
      },
      {
        id: "chatgpt-4",
        title: "I'm already using ChatGPT and want to go deeper",
        sub: "I need an ongoing reference I can come back to",
        tag: "Mobile web app",
        state: "live",
        href: "https://chatgpt.yoursmartstack.com",
        external: true,
        app: {
          productKey: "chatgpt-companion-app",
          blurb:
            "Prompts, Projects and custom GPTs set up around the work you actually do, explained in plain English from the first click.",
          name: "ChatGPT Companion App",
          checkoutUrl: "https://yoursmartstack.com/checkout/chatgpt/",
          price: "$147 one time",
        },
      },
    ],
  },
  "claude": {
    slug: "claude",
    name: "Claude",
    headline: "A calm way into Claude that",
    headlineAlt: "meets you where you are.",
    steps: [
      {
        id: "claude-1",
        title: "I want to try Claude, take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Claude",
        state: "live",
        href: "https://claude.ai/",
        external: true,
      },
      {
        id: "claude-2",
        title: "I want to understand 8 ways to think with Claude",
        sub: "Because the goal was never more output, it was clearer thinking",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/claude/8-wins",
        external: false,
      },
      {
        id: "claude-3",
        title: "I'm curious and want to see what's actually possible",
        sub: "I've poked around a little but want something concrete",
        tag: "14 Quick Wins: Interactive guide",
        state: "live",
        href: "/resources/claude/quick-wins-full",
        external: false,
      },
      {
        id: "claude-4",
        title: "I'm ready to implement, show me how it actually works",
        sub: "I want to feel what thinking with Claude is like in real time",
        tag: "Watch the video training",
        state: "live",
        href: "/trainings/stop-generating-start-refining",
        external: false,
      },
      {
        /* The Claude Companion App, the paid Smart Stack app.
         *
         * CORRECTED Aug 2026: this pointed at claude.carenglasser.com, an
         * address from before the Smart Stack domains. The app lives at
         * claude.yoursmartstack.com and nothing was serving the old host, so
         * the most committed step on this path — the one taken by people who
         * have already paid — went nowhere.
         *
         * Written out rather than read from `products.origin`, which is the
         * real source of truth, because this file is static content compiled
         * into the bundle and has no database to ask. billing.test.ts asserts
         * the two are equal, so they cannot drift again in silence — that test
         * needs a database and is in the tier that skips without one. */
        id: "claude-5",
        title: "I'm already using Claude and want to go deeper",
        sub: "I need an ongoing reference I can come back to",
        tag: "Mobile web app",
        state: "live",
        href: "https://claude.yoursmartstack.com",
        external: true,
        app: {
          productKey: "quickwins",
          blurb:
            "Proposals, long-form writing, and anything with a pile of files attached. Includes the Cowork walkthrough for handing Claude a real job and getting it done.",
          name: "Claude Companion App",
          checkoutUrl: "https://yoursmartstack.com/checkout/claude/",
          price: "$147 one time",
        },
      },
    ],
  },
  "gamma": {
    slug: "gamma",
    name: "Gamma",
    headline: "From idea to presentation,",
    headlineAlt: "in minutes, not hours.",
    steps: [
      {
        id: "gamma-1",
        title: "I want to try Gamma: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Gamma",
        state: "live",
        href: "https://gamma.app/signup?r=dbxr5two6g2pqgi",
        external: true,
      },
      {
        id: "gamma-2",
        title: "I want to understand 8 ways to think with Gamma",
        sub: "Because the best deck is not built slide by slide, it is built by describing what you need",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/gamma/8-ways",
        external: false,
      },
    ],
  },
  "gemini": {
    slug: "gemini",
    name: "Gemini",
    headline: "AI built into",
    headlineAlt: "the tools you already use.",
    steps: [
      {
        id: "gemini-1",
        title: "I want to try Gemini: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Gemini",
        state: "live",
        href: "https://gemini.google.com/",
        external: true,
      },
      {
        /* Reusing a placeholder id, not renumbering a live one. These were
           locked "coming soon" steps, and StepRow renders no completion
           control for a locked step, so nothing can have been ticked against
           them and there is no stored progress to reassign. Reuse an id whose
           step was ever live and you silently move somebody's tick onto
           different content. */
        id: "gemini-2",
        title: "I want to understand 8 ways to use Gemini",
        sub: "Because the goal was never more output, it was clearer thinking",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/gemini/8-ways",
        external: false,
      },
      {
        id: "gemini-3",
        title: "I want AI help inside Gmail, Docs and Calendar",
        sub: "10 Quick Wins for using Gemini as your everyday AI, wherever the work is",
        tag: "Quick Wins guide",
        state: "live",
        href: "/resources/gemini/quick-wins",
        external: false,
      },
      {
        id: "gemini-4",
        title: "I'm already using Gemini and want to go deeper",
        sub: "I need an ongoing reference I can come back to",
        tag: "Mobile web app",
        state: "live",
        href: "https://gemini.yoursmartstack.com",
        external: true,
        app: {
          productKey: "gemini-companion-app",
          blurb:
            "AI working quietly inside the Gmail, Docs and Drive you already live in. Nothing new to open, nothing new to learn.",
          name: "Gemini Companion App",
          checkoutUrl: "https://yoursmartstack.com/checkout/gemini/",
          price: "$147 one time",
        },
      },
    ],
  },
  "gemini-notebook": {
    slug: "gemini-notebook",
    name: "Gemini Notebook",
    headline: "Your documents:",
    headlineAlt: "finally working for you.",
    steps: [
      {
        id: "gemini-notebook-1",
        title: "I want to try Gemini Notebook, take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Gemini Notebook",
        state: "live",
        href: "https://notebooklm.google/",
        external: true,
      },
      {
        id: "gemini-notebook-2",
        title: "8 Ways to Remember With Gemini Notebook",
        sub: "Because the goal was never more information, it was less to carry",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/gemini-notebook/8-ways",
        external: false,
      },
      {
        id: "gemini-notebook-3",
        title: "Your documents, finally working for you",
        sub: "18 Quick Wins that show you how to use Gemini Notebook deliberately",
        tag: "Quick Wins guide",
        state: "live",
        href: "/resources/gemini-notebook/quick-wins",
        external: false,
      },
      {
        /* Numbered 5 with no 4 above it, deliberately. The Live Workshop step
           that sat at gemini-notebook-4 was removed; this id is a persisted
           progress key, so renumbering it to 4 would move every existing tick
           on the app step onto whatever took the number next. The gap is
           invisible to readers — the component numbers rows by position — and
           the alternative is silent data corruption. */
        id: "gemini-notebook-5",
        title: "I'm already using Gemini Notebook and want to go deeper",
        sub: "I need an ongoing reference I can come back to",
        tag: "Mobile web app",
        state: "live",
        /* No hyphen in the host, unlike the slug and the checkout path. That
           is what is actually served; the hyphenated form does not resolve. */
        href: "https://gemininotebook.yoursmartstack.com",
        external: true,
        app: {
          productKey: "gemini-notebook-companion-app",
          blurb:
            "Turn everything you have already written into a research partner. Client notes, transcripts, course drafts, your whole back catalogue.",
          name: "Gemini Notebook Companion App",
          checkoutUrl: "https://yoursmartstack.com/checkout/gemini-notebook/",
          price: "$147 one time",
        },
      },
    ],
  },
  "gohighlevel": {
    slug: "gohighlevel",
    name: "HighLevel",
    headline: "Your entire client system,",
    headlineAlt: "in one place.",
    steps: [
      {
        id: "gohighlevel-1",
        title: "I want to try GoHighLevel: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit GoHighLevel",
        state: "live",
        href: "https://www.gohighlevel.com/?fp_ref=lead31",
        external: true,
      },
    ],
  },
  "littlebird": {
    slug: "littlebird",
    name: "Littlebird",
    headline: "An AI that knows",
    headlineAlt: "exactly what you're working on.",
    steps: [
      {
        id: "littlebird-1",
        title: "I want to try Littlebird, take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Littlebird",
        state: "live",
        href: "https://try.littlebird.ai/caren-glasser",
        external: true,
      },
      {
        id: "littlebird-2",
        title: "8 Ways to Think With Littlebird",
        sub: "Practical prompts and use cases, ready to copy and try today",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/littlebird/8-ways",
        external: false,
      },
      {
        id: "littlebird-3",
        title: "Quick Wins Scenario Finder",
        sub: "Tell me where you are, and I'll show you the right Quick Win to start with",
        tag: "Interactive tool",
        state: "live",
        href: "/resources/littlebird/scenario-finder",
        external: false,
      },
      {
        id: "littlebird-4",
        title: "Get the Littlebird Quick Wins Guide",
        sub: "10 ways to use Littlebird from day one. Start with the one that fits where you are right now",
        tag: "Quick Wins guide",
        state: "live",
        href: "/resources/littlebird/quick-wins",
        external: false,
      },
    ],
  },
  "lovable": {
    slug: "lovable",
    name: "Lovable",
    headline: "A someday project,",
    headlineAlt: "turned into something people can click on.",
    steps: [
      {
        id: "lovable-1",
        title: "I want to try Lovable: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Lovable",
        state: "live",
        href: "https://lovable.dev/",
        external: true,
      },
      {
        id: "lovable-2",
        title: "I want to understand 8 ways to think with Lovable",
        sub: "Because you are not learning to code, you are learning to describe what you need clearly enough to build it",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/lovable/8-ways",
        external: false,
      },
    ],
  },
  "manus": {
    slug: "manus",
    name: "Manus",
    headline: "Let AI do the work,",
    headlineAlt: "not just answer the question.",
    steps: [
      {
        id: "manus-1",
        title: "I want to try Manus: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Manus",
        state: "live",
        href: "https://manus.im/invitation/TDNFQJ04TL7FG",
        external: true,
      },
      {
        /* Reusing a placeholder id, not renumbering a live one. These were
           locked "coming soon" steps, and StepRow renders no completion
           control for a locked step, so nothing can have been ticked against
           them and there is no stored progress to reassign. Reuse an id whose
           step was ever live and you silently move somebody's tick onto
           different content. */
        id: "manus-2",
        title: "I want to understand 8 ways to think with Manus",
        sub: "Because giving it a task is not the same as giving it a goal",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/manus/8-ways",
        external: false,
      },
      {
        id: "manus-3",
        title: "I'm ready to delegate a real task to Manus",
        sub: "10 Quick Wins for handing off the doing and staying in thinking mode",
        tag: "Quick Wins guide",
        state: "live",
        href: "/resources/manus/quick-wins",
        external: false,
      },
    ],
  },
  "open-video": {
    slug: "open-video",
    name: "Open.Video",
    headline: "Own your video content:",
    headlineAlt: "no platform can take it away.",
    steps: [
      {
        id: "open-video-1",
        title: "I'm ready to host my videos privately and take ownership",
        sub: "I want to stop relying on YouTube or Vimeo",
        tag: "Start free, no credit card",
        state: "live",
        href: "https://open.video/?a_aid=68f0226fc3294",
        external: true,
      },
      {
        id: "open-video-2",
        title: "I want to see how to use Open.Video with AI tools",
        sub: "I create AI-assisted content and need a private home for it",
        tag: "Hook Library: 30 copy-ready hooks",
        state: "live",
        href: "/resources/open-video/hook-library",
        external: false,
      },
      {
        id: "open-video-3",
        title: "I want commands, SEO tips, and best practices in one place",
        sub: "I need a quick reference I can come back to",
        tag: "Commands & Best Practices: tabbed reference card",
        state: "live",
        href: "/resources/open-video/reference-card",
        external: false,
      },
      {
        id: "open-video-4",
        title: "I want a day-by-day action plan to launch my channel",
        sub: "I need structure to go from zero to live in 30 days",
        tag: "30-Day Launch Calendar: checkable action plan",
        state: "live",
        href: "/resources/open-video/30-day-calendar",
        external: false,
      },
      {
        id: "open-video-5",
        title: "I want to stop renting my audience and own my platform",
        sub: "I'm ready to learn the strategy behind video ownership",
        tag: "Watch the video training",
        state: "live",
        href: "/trainings/stop-renting-your-audience",
        external: false,
      },
    ],
  },
  "opus-clip": {
    slug: "opus-clip",
    name: "Opus Clip",
    headline: "Your long videos,",
    headlineAlt: "turned into clips that get watched.",
    steps: [
      {
        id: "opus-clip-1",
        title: "I want to try Opus Clip: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Opus Clip",
        state: "live",
        href: "https://www.opus.pro/?via=carenglasser",
        external: true,
      },
      {
        /* Reusing the id the removed placeholder had. Safe for the same
           reason it is safe on the perplexity path: opus-clip-2 was never
           anything but a locked "soon" row, and a locked step renders no
           completion control, so nobody can have a tick stored against it.
           Reuse an id whose step was ever live and you silently move
           somebody's progress from one piece of content to another. */
        id: "opus-clip-2",
        title: "I want to know what to do at each step, without guessing",
        sub: "The whole workflow on one page, plus the checklist to run before a clip goes out",
        tag: "Cheat sheet",
        state: "live",
        href: "/resources/opus-clip/cheat-sheet",
        external: false,
      },
      {
        id: "opus-clip-3",
        title: "I want to build a consistent short-form presence without extra recording",
        sub: "I need a system that clips, captions, and posts automatically",
        tag: "Watch the video training",
        state: "live",
        href: "/trainings/automate-your-social-media",
        external: false,
      },
    ],
  },
  "perplexity": {
    slug: "perplexity",
    name: "Perplexity",
    headline: "Research faster,",
    headlineAlt: "with answers you can trust.",
    steps: [
      {
        id: "perplexity-1",
        title: "I want to try Perplexity: take me there",
        sub: "I'm ready to explore it directly",
        tag: "Visit Perplexity",
        state: "live",
        href: "https://pplx.ai/carenglasser",
        external: true,
      },
      {
        /* These two ids were locked "coming soon" placeholders and are now the
           guides they were promising. Reusing the ids rather than retiring
           them is safe *because* they were locked: StepRow renders no
           completion control for a locked step, so nothing can have been
           marked done against them and there is no stored progress to
           reassign. Reuse an id whose step was ever live and you silently move
           somebody's tick from one piece of content to another. */
        id: "perplexity-2",
        title: "I want to understand 8 ways to research with Perplexity",
        sub: "Because the goal was never more information, it was a clearer decision",
        tag: "Interactive guide",
        state: "live",
        href: "/resources/perplexity/8-ways",
        external: false,
      },
      {
        id: "perplexity-3",
        title: "I want to stop Googling and start getting real answers",
        sub: "10 Quick Wins for getting oriented fast and stopping at enough",
        tag: "Quick Wins guide",
        state: "live",
        href: "/resources/perplexity/quick-wins",
        external: false,
      },
      {
        id: "perplexity-4",
        title: "I'm already using Perplexity and want to go deeper",
        sub: "I need an ongoing reference I can come back to",
        tag: "Mobile web app",
        state: "live",
        href: "https://perplexity.yoursmartstack.com",
        external: true,
        app: {
          productKey: "perplexity-companion-app",
          blurb:
            "Research with the sources attached, so you can hand it to a client without checking every line twice.",
          name: "Perplexity Companion App",
          checkoutUrl: "https://yoursmartstack.com/checkout/perplexity/",
          price: "$147 one time",
        },
      },
    ],
  },
};

export const toolPath = (slug: string): ToolPath | undefined => TOOL_PATHS[slug];

/**
 * The steps that count towards having worked through a path.
 *
 * Two things are excluded, for different reasons.
 *
 * The companion-app step is not something you do, it is something you buy.
 * EcosystemPath treats owning the app as that row being done, which is the
 * honest reading, but it is not progress through the guides and it cannot be
 * ticked. A path with an app therefore "finishes" at the last guide.
 *
 * `state: "soon"` steps are excluded because they cannot be completed. They
 * still count in the denominator the progress bar shows — somebody on a path
 * meant to have three steps should not be told they are at 100% having done
 * the one that exists — but requiring them here would mean a path with a
 * pending step could never be finished by anybody.
 *
 * Shared with the server, which decides from exactly this set whether to tell
 * GoHighLevel that somebody has finished a path. That is the whole reason it
 * is a function rather than a filter written out at each call site: the tag
 * and the progress bar have to mean the same thing, and two copies of this
 * line would eventually disagree about somebody's last step.
 */
export const guideSteps = (path: ToolPath): PathStep[] =>
  path.steps.filter(s => !s.app && s.state === "live");

/** Whether this path ends in something to buy. Changes the tag, not the rule. */
export const hasCompanionApp = (path: ToolPath): boolean =>
  path.steps.some(s => Boolean(s.app));
